import React, {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import './FilterDrawer.css';

import { FilterAltOutlined, MoreInfoOutlined } from '@duxton-web/icons';
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  TimeRangePickerProps,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import Select from '@duxton-web/ui/es/select';
import dayjs from 'dayjs';

import CountrySelector from '../CountrySelector';
import ProductEntitySelect from './ProductEntitySelect';
import VariablesSelection from './VariablesSelection';
import { FilterType, FilterType_Keys } from '@/common/const/filterTypes';
import { SelectType, SelectType_Keys } from '@/common/const/selectTypes';
import { CloseIcon } from '@/common/Icons';
import BackendSelect from '@/common/selects/BackendSelect';
import CityTreeSelect from '@/common/selects/CityTreeSelect';
import CommonSelect from '@/common/selects/CommonSelect';
import VehicleTreeSelect from '@/common/selects/VehicleTreeSelect';
import { SHORT_DATE_TIME } from '@/constants/dateTime';
import { FACETS } from '@/constants/facets';
import Service from '@/lib/App';
import { VariableStateTagStyle } from '@/pages/Variable/components/VariableStateTag/VariableStateTag';
import { getLoggedUserEmail } from '@/utils/user';

export enum ConstraintFilterKey {
  FACET_TYPE = 'facet_type',
  FACET_VALUE = 'facet_value',
}

interface OptionProps {
  label: string;
  value: string;
  color?: string;
  style?: CSSProperties;
}

export type Filter = {
  name: string;
  label: string;
  placeholder?: string;
  type?: (typeof FilterType)[FilterType_Keys];
  multiple?: boolean;
  selectType?: (typeof SelectType)[SelectType_Keys];
  options?: OptionProps[];
  customControl?: ReactNode;
  infoBannerText?: string;
  disabled?: boolean;
  disabledDate?: (date?: dayjs.Dayjs) => boolean;
  rangePresets?: TimeRangePickerProps['presets'];
};

export type Group = {
  label: string;
  fields: Filter[];
};

interface OwnProps {
  id?: string;
  key?: string;
  testId?: string;
  className?: string;
  /** section and its fields to be rendered in the filter drawer. this will take priority over fields prop */
  groups?: Group[];
  /** the fields to be rendered in the filter drawer*/
  fields?: Filter[];
  /** set the filter drawer form fields by passing the form fields and its value as object */
  filters?: { [key: string]: any };
  /** filter keys to be ignored */
  ignoreFilters?: string[];
  /** filter keys to be excluded from count */
  filtersExcludeFromCount?: string[];
  onApply: (filters: { [key: string]: any }) => void;
  onFilterClick?: () => void;
}

export const getControlTestId = (
  type: (typeof FilterType)[FilterType_Keys],
): string => {
  return `filter-drawer-control-${type}`;
};

export const getSelectTestId = (
  selectType: (typeof SelectType)[SelectType_Keys],
): string => {
  return `filter-drawer-select-${selectType}`;
};

const FilterDrawer: FC<OwnProps> = ({
  className,
  testId = '',
  fields,
  groups,
  filters = {},
  ignoreFilters = [],
  filtersExcludeFromCount = [],
  onApply = () => {},
  onFilterClick = () => {},
}) => {
  const [filterMetaForm] = Form.useForm();
  const [lastAppliedFilters, setLastAppliedFilters] = useState<{
    [key: string]: any;
  }>({});
  const [open, setOpen] = useState<boolean>(false);
  const [numOfApplcableFilters, setNumOfApplicableFilters] =
    useState<number>(0);
  const [numOfNonApplicableFilters, setNumOfNonApplicableFilters] =
    useState<number>(0);
  const [filterKeys, setFilterKeys] = useState<string[]>([]);

  const [filterConstraint, setFilterConstraint] = useState({
    [ConstraintFilterKey.FACET_TYPE]: '',
    [ConstraintFilterKey.FACET_VALUE]: '',
  });

  const transformValueByType = (type: string, value: any) => {
    switch (type) {
      case FilterType.NUMBER:
        return parseInt(value, 10);
      case FilterType.BOOLEAN:
        return Boolean(value);
      case FilterType.DATE_RANGE:
      case FilterType.DATE_TIME_RANGE:
        return [
          value?.[0] ? dayjs(value[0]) : null,
          value?.[1] ? dayjs(value[1]) : null,
        ];
      default:
        return value;
    }
  };

  const getFieldByName = (name: string): Filter => {
    return fields?.find((field) => field.name === name);
  };

  const getFieldByFilterKey = (filterKey: string): Filter => {
    if (groups?.length) {
      let field = null;

      groups.forEach((group) => {
        if (field) return false;

        field = group?.fields?.find((field) => field?.name === filterKey);
      });

      return field;
    }

    return fields.find((field) => field?.name === filterKey);
  };

  const updateFormFieldValue = (
    filters: { [key: string]: any },
    filterKeys: string[],
  ) => {
    let appliedFilterCount: number = 0;

    const filtersKeyArr = Object.keys(filters);

    filterKeys.forEach((key) => {
      if (!filters?.[key]) {
        filterMetaForm.setFieldValue(key, null);
        return;
      }

      const field = getFieldByName(key);
      const transformedValue = transformValueByType(field?.type, filters[key]);

      filterMetaForm.setFieldValue(key, transformedValue);

      if (!filtersExcludeFromCount.includes(key)) appliedFilterCount += 1;
    });

    setNumOfApplicableFilters(appliedFilterCount);
    setNumOfNonApplicableFilters(
      filtersKeyArr.filter(
        (key) =>
          !filterKeys.includes(key) &&
          !ignoreFilters.includes(key) &&
          !filtersExcludeFromCount.includes(key),
      ).length,
    );
  };

  const setAndUpdateFormFields = (filterKeys: string[]) => {
    setFilterKeys(filterKeys);

    updateFormFieldValue(filters, filterKeys);
  };

  useEffect(() => {
    setNumOfApplicableFilters(0);
    setNumOfNonApplicableFilters(0);

    if (!Object.keys(filters).length) {
      filterMetaForm.resetFields();

      return;
    }

    const incomingFilterKeys: string[] = [];

    const appendFilters = (filters: Filter[]) => {
      if (!filters) return;

      filters.forEach((filter) => {
        if (!filter?.name) return;

        incomingFilterKeys.push(filter.name);
      });
    };

    if (groups?.length) {
      groups.forEach((group) => {
        appendFilters(group?.fields);
      });

      setAndUpdateFormFields(incomingFilterKeys);

      return;
    }

    appendFilters(fields);

    setAndUpdateFormFields(incomingFilterKeys);
  }, [filters]);

  useEffect(() => {
    const payload = {};

    filterKeys.forEach((key) => {
      if (filterMetaForm.getFieldValue(key))
        payload[key] = filterMetaForm.getFieldValue(key);
    });

    setLastAppliedFilters(payload);
  }, [open]);

  const applyFilters = () => {
    const newFilters = { ...filterMetaForm.getFieldsValue() };
    const finalFilters = {
      ...filters,
      ...newFilters,
    };

    Object.keys(finalFilters).forEach((key) => {
      if (!finalFilters[key]) {
        delete finalFilters[key];
        return false;
      }

      const field = getFieldByFilterKey(key);
      if (field?.type === FilterType.DATE_RANGE) {
        const dates = finalFilters[key];

        const start = dayjs(dates[0]).format('YYYY-MM-DD');
        const end = dayjs(dates[1]).format('YYYY-MM-DD');

        finalFilters[key] = [start, end];
      }
      if (field?.type === FilterType.DATE_TIME_RANGE) {
        const dates = finalFilters[key];

        const start = dayjs(dates[0]).format(SHORT_DATE_TIME);
        const end = dayjs(dates[1]).format(SHORT_DATE_TIME);

        finalFilters[key] = [start, end];
      }

      if (field?.type === FilterType.CONSTRAINTS) {
        const constraintFacetType =
          filterConstraint[ConstraintFilterKey.FACET_TYPE];
        const constraintFacetValue =
          filterConstraint[ConstraintFilterKey.FACET_VALUE];
        if (constraintFacetType && constraintFacetValue) {
          finalFilters[key] = {
            [ConstraintFilterKey.FACET_TYPE]: constraintFacetType,
            [ConstraintFilterKey.FACET_VALUE]: constraintFacetValue,
          };
        }
      }
    });

    onApply(finalFilters);

    setOpen(false);
  };

  const renderControlByType = (filter: Filter) => {
    const { name, placeholder, type, disabled, disabledDate, rangePresets } =
      filter;

    const controlTestId = getControlTestId(type);

    switch (type) {
      case FilterType.STRING:
        return (
          <Input
            disabled={disabled}
            key={`input-${name}`}
            data-testid={controlTestId}
            placeholder={placeholder}
          />
        );
      case FilterType.NUMBER:
        return (
          <InputNumber
            disabled={disabled}
            key={`input-number-${name}`}
            data-testid={controlTestId}
            placeholder={placeholder}
          />
        );
      case FilterType.BOOLEAN:
        return (
          <Switch
            disabled={disabled}
            key={`switch-${name}`}
            data-testid={controlTestId}
            style={{
              width: '32px',
            }}
          />
        );
      case FilterType.DATE_RANGE:
        return (
          <DatePicker.RangePicker
            disabled={disabled}
            data-testid={controlTestId}
            style={{
              width: '100%',
            }}
            format={'YYYY-MM-DD'}
          />
        );

      case FilterType.DATE_TIME_RANGE:
        return (
          <DatePicker.RangePicker
            disabled={disabled}
            showTime={{
              format: 'HH:mm',
              defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
            }}
            data-testid={controlTestId}
            style={{
              width: '100%',
            }}
            disabledDate={disabledDate}
            presets={rangePresets}
            format={SHORT_DATE_TIME}
          />
        );

      case FilterType.CONSTRAINTS:
        return (
          <Flex gap={8} style={{ maxWidth: '100%' }}>
            <Select
              options={[
                { value: FACETS.Country, label: 'Country' },
                { value: FACETS.City, label: 'City' },
                { value: FACETS.VehicleTypeID, label: 'Vehicle Type' },
              ]}
              disabled={disabled}
              style={{ minWidth: 'calc(50% - 4px)', flex: 1 }}
              value={filterConstraint[ConstraintFilterKey.FACET_TYPE]}
              onChange={(value) => {
                filterMetaForm.setFieldValue(name, value);
                setFilterConstraint({
                  [ConstraintFilterKey.FACET_TYPE]: value,
                  [ConstraintFilterKey.FACET_VALUE]: '',
                });
              }}
            />
            <div style={{ minWidth: 'calc(50% - 4px)', flex: 1 }}>
              {!filterConstraint[ConstraintFilterKey.FACET_TYPE] && (
                <Select options={[]} disabled />
              )}
              {filterConstraint[ConstraintFilterKey.FACET_TYPE] ===
                FACETS.Country && (
                <CountrySelector
                  disabled={disabled}
                  value={filterConstraint[ConstraintFilterKey.FACET_VALUE]}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setFilterConstraint({
                        ...filterConstraint,
                        [ConstraintFilterKey.FACET_VALUE]: value,
                      });
                    }
                  }}
                />
              )}
              {filterConstraint[ConstraintFilterKey.FACET_TYPE] ===
                FACETS.City && (
                <CityTreeSelect
                  popupMatchSelectWidth={false}
                  disabled={disabled}
                  value={filterConstraint[ConstraintFilterKey.FACET_VALUE]}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setFilterConstraint({
                        ...filterConstraint,
                        [ConstraintFilterKey.FACET_VALUE]: value,
                      });
                    }
                  }}
                />
              )}
              {filterConstraint[ConstraintFilterKey.FACET_TYPE] ===
                FACETS.VehicleTypeID && (
                <VehicleTreeSelect
                  popupMatchSelectWidth={false}
                  disabled={disabled}
                  value={filterConstraint[ConstraintFilterKey.FACET_VALUE]}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setFilterConstraint({
                        ...filterConstraint,
                        [ConstraintFilterKey.FACET_VALUE]: value,
                      });
                    }
                  }}
                />
              )}
            </div>
          </Flex>
        );
    }
  };

  const getUsersWithEmail = () => {
    const currentUserEmail = getLoggedUserEmail();
    const currentUser = currentUserEmail
      ? Service.cache!.getBasicUsers().find(
          (user) => user.email === currentUserEmail,
        )
      : null;
    const currentUserValue = currentUser
      ? Service.cache!.getBasicUsers().find(
          ({ email }) => currentUser.email === email,
        )
      : null;
    const usersWithEmail = Service.cache!.getBasicUsers().filter(
      ({ email }) =>
        (!currentUserValue && email) ||
        (currentUserValue && currentUserValue.email !== email),
    );

    if (currentUserValue && usersWithEmail)
      usersWithEmail.splice(0, 1, currentUserValue);

    return usersWithEmail;
  };

  const renderControlBySelectType = (filter: Filter) => {
    const { selectType, name, placeholder, disabled, multiple } = filter;
    const selectTestId = getSelectTestId(selectType);

    switch (selectType) {
      case SelectType.TYPE:
        return (
          <CommonSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            data-testid={selectTestId}
            placeholder={placeholder}
            values={Service.cache!.getAllVariableTypes().map((val) => ({
              id: val,
            }))}
          />
        );
      case SelectType.USER: {
        return (
          <CommonSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            data-testid={selectTestId}
            idKey="email"
            values={getUsersWithEmail()}
            placeholder={placeholder}
          />
        );
      }
      case SelectType.USER_V2: {
        return (
          <Select
            disabled={disabled}
            key={`select-${name}`}
            data-testid={selectTestId}
            placeholder={placeholder}
            allowClear
            showSearch
            options={getUsersWithEmail().map((user) => ({
              value: user.email,
              label: user.email,
            }))}
          />
        );
      }
      case SelectType.TECH_FAMILY:
        return (
          <CommonSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            idKey="prefix"
            data-testid={selectTestId}
            valueFn={(item) => <div>{`${item?.title} (${item?.prefix})`}</div>}
            values={Service.cache!.getAllTechFamilies()}
            placeholder={placeholder}
          />
        );
      case SelectType.BACKEND:
      case SelectType.SUBSCRIBED_SERVICES:
        return (
          <BackendSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            data-testid={selectTestId}
            placeholder={placeholder}
          />
        );
      case SelectType.DOMAIN:
        return (
          <CommonSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            idKey="name"
            data-testid={selectTestId}
            labelKey="title"
            descriptionKey="description"
            values={Service.cache.getAllDomainMetadata()}
            placeholder={placeholder}
          />
        );
      case SelectType.FACET:
        return (
          <CommonSelect
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            data-testid={selectTestId}
            values={Service.cache!.getAllFacets()}
            idKey="name"
            valueKey="title"
            multiple={multiple}
            placeholder={placeholder}
          />
        );
      case SelectType.COUNTRY:
        return (
          <CountrySelector
            disabled={disabled}
            key={`select-${name}`}
            allowClear
            data-testid={selectTestId}
            multiple={multiple}
            placeholder={placeholder}
          />
        );
      case SelectType.CITY:
        return (
          <Select
            disabled={disabled}
            data-testid={selectTestId}
            placeholder={placeholder}
            allowClear
            showSearch
          >
            {Service.cache.getAllCities().map((city) => (
              <Select.Option key={city.id} value={city.name}>
                {city.name}
              </Select.Option>
            ))}
          </Select>
        );
      case SelectType.TAG:
        return (
          <Select
            disabled={disabled}
            data-testid={selectTestId}
            placeholder={placeholder}
            allowClear
            showSearch
          >
            {Service.cache.getAllExperimentTags().map((tag) => (
              <Select.Option key={tag} value={tag}>
                {tag}
              </Select.Option>
            ))}
          </Select>
        );
      case SelectType.VARIABLE:
        return (
          <VariablesSelection
            disabled={disabled}
            variableName={filters?.[name]}
            onChange={(value: any) => filterMetaForm.setFieldValue(name, value)}
            placeholder={placeholder}
          />
        );
      case SelectType.PRODUCT_ENTITY:
        return (
          <ProductEntitySelect
            disabled={disabled}
            productEntity={filters?.[name]}
            onChange={(value: any) => filterMetaForm.setFieldValue(name, value)}
            placeholder={placeholder}
          />
        );
    }
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    let color = 'default';
    let style = null;

    if (VariableStateTagStyle[value]) {
      color = VariableStateTagStyle[value].color;
      style = VariableStateTagStyle[value].style;
    }

    return (
      <Tag
        color={color}
        style={{ ...style, marginRight: 3 }}
        bordered={false}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
    );
  };

  const renderFieldControl = (field: Filter) => {
    const {
      type,
      selectType,
      options,
      customControl,
      placeholder,
      disabled,
      multiple,
    } = field;
    if (!type && !selectType && !options?.length && !customControl) return null;

    const standardControl = renderControlByType(field);

    if (standardControl) return standardControl;

    const selectTypeControl = renderControlBySelectType(field);

    if (selectTypeControl) return selectTypeControl;

    if (options?.length) {
      if (multiple)
        return (
          <Select
            tagRender={tagRender}
            options={options}
            disabled={disabled}
            placeholder={placeholder}
            allowClear
            mode="multiple"
          />
        );

      return (
        <Select
          tagRender={tagRender}
          options={options}
          disabled={disabled}
          placeholder={placeholder}
          allowClear
        />
      );
    }

    return field.customControl;
  };

  function renderInfoBanner(filter: Filter) {
    if (!filter.infoBannerText) return false;

    return (
      <Alert
        style={{
          marginTop: '4px',
          color: '#824703',
        }}
        type="warning"
        message={
          <Flex align="center" gap={8}>
            <MoreInfoOutlined
              style={{
                alignSelf: 'flex-start',
                color: '#F09800',
                width: '20px',
                height: '20px',
              }}
            />
            {filter.infoBannerText}
          </Flex>
        }
      />
    );
  }

  const renderFields = () => {
    if (!fields?.length && !groups?.length)
      return <Alert type="warning" message="No filters provided" />;

    if (groups?.length)
      return (
        <ConfigProvider
          theme={{
            components: {
              Form: {
                itemMarginBottom: 0,
              },
            },
          }}
        >
          <Form layout="vertical" form={filterMetaForm}>
            <Flex vertical gap={16}>
              {groups.map((group) => (
                <Flex
                  key={`filter-drawer-group-${group.label
                    .replace(' ', '')
                    .toLowerCase()}`}
                  vertical
                  gap={12}
                >
                  <Typography.Text
                    strong
                    data-testid={`filter-drawer-group-${group.label.replace(
                      ' ',
                      '-',
                    )}`}
                  >
                    {group.label}
                  </Typography.Text>
                  {group?.fields &&
                    group.fields.map((field) => (
                      <>
                        <Form.Item
                          key={`filter-drawer-form-item-${field.name}`}
                          name={field.name}
                          label={field.label}
                          valuePropName={
                            field?.type === FilterType.BOOLEAN
                              ? 'checked'
                              : 'value'
                          }
                        >
                          {renderFieldControl(field)}
                        </Form.Item>
                        {renderInfoBanner(field)}
                      </>
                    ))}
                </Flex>
              ))}
            </Flex>
          </Form>
        </ConfigProvider>
      );

    return (
      <ConfigProvider
        theme={{
          components: {
            Form: {
              itemMarginBottom: 0,
            },
          },
        }}
      >
        <Form layout="vertical" form={filterMetaForm}>
          <Flex vertical gap={12}>
            {fields.map((field) => (
              <>
                <Form.Item
                  key={`filter-drawer-form-item-${field.name}`}
                  name={field.name}
                  label={field.label}
                  valuePropName={
                    field?.type === FilterType.BOOLEAN ? 'checked' : 'value'
                  }
                >
                  {renderFieldControl(field)}
                </Form.Item>
                {renderInfoBanner(field)}
              </>
            ))}
          </Flex>
        </Form>
      </ConfigProvider>
    );
  };

  function renderFilterButton() {
    return (
      <Tooltip title="Click here to filter results">
        <Button
          data-testid={testId ? testId : 'filter-btn'}
          shape="round"
          type={numOfNonApplicableFilters ? 'default' : 'secondary'}
          size="small"
          style={{
            width: 'fit-content',
          }}
          className={
            numOfNonApplicableFilters
              ? 'grab-x__filter-drawer-non-applicable-filter'
              : ''
          }
          onClick={() => {
            setOpen(true);

            onFilterClick();
          }}
        >
          <Flex align="center" gap={4} justify="center">
            <FilterAltOutlined
              color={numOfNonApplicableFilters ? '#f09800' : ''}
            />
            {numOfApplcableFilters
              ? `${numOfApplcableFilters} ${
                  numOfApplcableFilters > 1 ? 'filters' : 'filter'
                } applied`
              : 'Filters'}
          </Flex>
        </Button>
      </Tooltip>
    );
  }

  const renderFilterDrawer = () => {
    if (!open) return false;

    return (
      <Drawer
        className={`grab-x__filter-drawer${className ? ` ${className}` : ''}`}
        styles={{
          header: {
            padding: '32px 32px 16px 32px',
          },
          footer: {
            padding: 24,
          },
        }}
        closable={false}
        title={
          <Flex
            align="center"
            justify="space-between"
            style={{
              cursor: 'pointer',
            }}
          >
            <ConfigProvider
              theme={{
                components: {
                  Typography: {
                    titleMarginBottom: 0,
                  },
                },
              }}
            >
              <Typography.Title level={3}>Filters</Typography.Title>
            </ConfigProvider>

            <CloseIcon
              data-testid="filter-close-btn"
              onClick={() => {
                const payload = {
                  ...filters,
                  ...lastAppliedFilters,
                };

                updateFormFieldValue(payload, filterKeys);

                setOpen(false);
              }}
            />
          </Flex>
        }
        open={true}
        footer={
          <Flex align="center" justify="space-between">
            <Button
              size="small"
              onClick={() => {
                filterMetaForm.resetFields();
                setFilterConstraint({
                  [ConstraintFilterKey.FACET_TYPE]: '',
                  [ConstraintFilterKey.FACET_VALUE]: '',
                });
                applyFilters();

                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button type="primary" size="small" onClick={applyFilters}>
              Apply Filters
            </Button>
          </Flex>
        }
      >
        {renderFields()}
      </Drawer>
    );
  };

  return (
    <>
      {renderFilterButton()}
      {renderFilterDrawer()}
    </>
  );
};

export default FilterDrawer;
